import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, EventEmitter, HostBinding, inject, Input, Output } from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { RouterModule } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { DashboardProfileContextService, DashboardProfileUserContextService } from '@rma/authentication/public/user';
import { CurrentSubscriptionService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { StaticImageUrlPipe } from '@rma/generic/util/pipes/static-image-url';
import { TypeGuardPipe } from '@rma/generic/util/pipes/type-guard';
import { LG_RENOWNED_LOGO_PATH, SM_RENOWNED_LOGO_PATH } from '@rma/layout/public/util-layout';
import { BrandingFacadeService } from '@rma/profile/public/api-branding';
import { ProfileDetailsContextService } from '@rma/profile/public/api-profile-details';
import { map } from 'rxjs';

import { isItem, isMenu } from '../domain/entities/side-nav-items';
import { SideNavigationService } from '../domain/services/side-navigation.service';
import { AccountsFooterComponent } from '../ui-nav-accounts-footer/nav-accounts-footer.component';
import { NavMenuItemComponent } from '../ui-nav-menu-item/nav-menu-item.component';
import { NavProfileMenuComponent } from '../ui-nav-profile-menu/nav-profile-menu.component';
import { SubMenuComponent } from '../ui-sub-menu/sub-menu.component';
import { IsNavMenuItemActivePipe } from '../util-is-nav-menu-item-active/is-nav-menu-item-active.pipe';
import { IsSubMenuOpenPipe } from '../util-is-sub-menu-open/is-sub-menu-open.pipe';

@Component({
  selector: 'rma-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  imports: [
    AccountsFooterComponent,
    AsyncPipe,
    IsNavMenuItemActivePipe,
    IsSubMenuOpenPipe,
    MatListModule,
    NavMenuItemComponent,
    NavProfileMenuComponent,
    RmaIconComponent,
    RouterModule,
    StaticImageUrlPipe,
    SubMenuComponent,
    TranslocoModule,
    TypeGuardPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideTranslocoScope('sideNav')],
})
export class SideNavComponent {
  @HostBinding('class.mobile-nav-visible')
  @Input()
  public visible = false;

  @Input()
  public collapsed = false;

  @Output()
  public readonly navigated = new EventEmitter<void>();

  @Output()
  public readonly subMenuExpanded = new EventEmitter<void>();

  @Output()
  public readonly toggleSideNav = new EventEmitter<boolean>();

  private readonly brandingFacade = inject(BrandingFacadeService);
  private readonly currentSubscriptionService = inject(CurrentSubscriptionService);
  private readonly document = inject(DOCUMENT);
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly profileDetailsService = inject(ProfileDetailsContextService);
  private readonly profileUserContextService = inject(DashboardProfileUserContextService);

  protected readonly sideNavigationService = inject(SideNavigationService);

  protected readonly isMenu = isMenu;
  protected readonly isItem = isItem;

  protected readonly profileContext$ = this.profileContextService.profileContext$;
  protected readonly backgroundColour$ = this.brandingFacade.selectedTheme$.pipe(map(({ backgroundColour }) => backgroundColour ?? null));

  protected readonly currentPlanName$ = this.currentSubscriptionService.currentSubscriptionSummary$.pipe(
    map((currentSubscription) => currentSubscription?.displayName ?? ''),
  );

  //This should be cleaned up when some one has times as this is one of the only places that uses this
  protected readonly profileUser$ = this.profileUserContextService.profileUserContext$;

  protected readonly profileRating$ = this.profileDetailsService.profileSummary$.pipe(
    map(({ ratingValue, reviewCount }) => ({ ratingValue, reviewCount })),
  );

  protected readonly agencyName$ = this.profileContextService.profileContext$.pipe(map(({ parentName }) => parentName ?? null));

  protected readonly lgRenownedLogoPath = LG_RENOWNED_LOGO_PATH;
  protected readonly smRenownedLogoPath = SM_RENOWNED_LOGO_PATH;

  public onNavigate(): void {
    this.navigated.emit();
  }

  public onMenuExpanded(id: string) {
    if (this.collapsed) {
      this.subMenuExpanded.emit();

      setTimeout(() => {
        const subMenu = this.document.querySelector(`[data-id='${id}']`);

        if (subMenu) {
          subMenu.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }, 300);
    }
  }
}
